"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import { Button } from "@carpybara/ui/shadcn/button";
import {
  forcedRouteErrorRecoveryHref,
  routeErrorRecoveryHref,
  isStaleChunkError,
  shouldReloadRouteErrorOnce,
} from "@/lib/stale-chunk";
import { useShell } from "@/components/page-shell";

type RouteErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function RouteError({ error, reset }: RouteErrorProps) {
  const { t } = useShell();

  useEffect(() => {
    const staleChunk = isStaleChunkError(error);
    if (!staleChunk) {
      Sentry.captureException(error);
    }
    const recoveryHref = routeErrorRecoveryHref(window.location.href);
    if (
      recoveryHref &&
      shouldReloadRouteErrorOnce(
        error,
        window.location.pathname,
        window.sessionStorage,
      )
    ) {
      // A changed query guarantees a fresh document in embedded iOS browsers;
      // replace keeps Back from returning to the broken history entry.
      window.location.replace(recoveryHref);
      return;
    }
  }, [error]);

  const handleReload = () => {
    const recoveryHref = forcedRouteErrorRecoveryHref(window.location.href);
    if (recoveryHref) {
      window.location.replace(recoveryHref);
      return;
    }
    window.location.reload();
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 py-10 text-center"
    >
      <h2 className="text-xl font-semibold text-(--text-primary)">
        {t("common.error")}
      </h2>
      <p className="max-w-md text-sm text-(--text-muted)">
        {t("errorPage.message")}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={reset}
          aria-label={t("errorPage.retryAria")}
        >
          {t("common.retry")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReload}
          aria-label={t("errorPage.reloadAria")}
        >
          {t("errorPage.reload")}
        </Button>
      </div>
      {error.digest && (
        <p className="text-[11px] text-(--text-faint)">
          {t("errorPage.reference")}{" "}
          <span className="font-mono">{error.digest}</span>
        </p>
      )}
    </div>
  );
}
